import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import config from '../../../../../auth_config.json';
import { ApiResponse, ComponentSummary, ComponentDetail, ComponentType, SafetyGoal, AnalysisRow, FailureEffectOption, SystemEffectOption, FailureInstanceOption, SafetyMechanismOption, CoverageLink, ThermalModelOption, StandardOption, IdSuggestions, TelesisNetlistUploadResult, SmartPdfUploadResult, Project } from '../models';
import { Observable } from 'rxjs';
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = config.fmedaApiUri;



  getComponents() {
    return this.http.get<ApiResponse<ComponentSummary[]>>(`${this.apiBaseUrl}/api/components`);
  }

  getComponentDetail(componentDesignator: string) {
    return this.http.get<ApiResponse<ComponentDetail>>(`${this.apiBaseUrl}/api/components/${encodeURIComponent(componentDesignator)}`);
  }

  getComponentTypes() {
    return this.http.get<ApiResponse<ComponentType[]>>(`${this.apiBaseUrl}/api/component-types`);
  }

  getSafetyGoals() {
    return this.http.get<ApiResponse<SafetyGoal[]>>(`${this.apiBaseUrl}/api/safety-goals`);
  }

  getAnalysis() {
    return this.http.get<ApiResponse<AnalysisRow[]>>(`${this.apiBaseUrl}/api/analysis`);
  }

  getFailureEffects() {
    return this.http.get<ApiResponse<FailureEffectOption[]>>(`${this.apiBaseUrl}/api/failure-effects`);
  }

  getSystemEffects() {
    return this.http.get<ApiResponse<SystemEffectOption[]>>(`${this.apiBaseUrl}/api/system-effects`);
  }

  getFailureInstances() {
    return this.http.get<ApiResponse<FailureInstanceOption[]>>(`${this.apiBaseUrl}/api/failure-instances`);
  }

  getSafetyMechanisms() {
    return this.http.get<ApiResponse<SafetyMechanismOption[]>>(`${this.apiBaseUrl}/api/safety-mechanisms`);
  }

  getCoverageLinks() {
    return this.http.get<ApiResponse<CoverageLink[]>>(`${this.apiBaseUrl}/api/coverage-links`);
  }

  getThermalModels() {
    return this.http.get<ApiResponse<ThermalModelOption[]>>(`${this.apiBaseUrl}/api/thermal-models`);
  }

  getStandards() {
    return this.http.get<ApiResponse<StandardOption[]>>(`${this.apiBaseUrl}/api/standards`);
  }

  getIdSuggestions() {
    return this.http.get<ApiResponse<IdSuggestions>>(`${this.apiBaseUrl}/api/id-suggestions`);
  }

  seedDemoData() {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/admin/seed`, {});
  }

  clearDatabase(nodeTypes: string[]) {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/admin/clear`, { node_types: nodeTypes });
  }

  createComponentType(payload: any) {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/component-types`, payload);
  }

  createComponent(payload: any) {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/components`, payload);
  }
  createFailureLink(payload: any) {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/failure-links`, payload);
  }
  createFailureInstance(payload: { component_designator: string; failure_mode_code: string; fi_name: string; fi_id?: string; }) {
    return this.http.post<ApiResponse<{ message: string; failure_instance_id: string }>>(
      `${this.apiBaseUrl}/api/failure-instances`,
      payload
    );
  }
  createSafetyMechanism(payload: any) {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiBaseUrl}/api/safety-mechanisms`, payload);
  }

  createSafetyGoal(payload: { description: string; id?: string }) {
    // Pass the expected return type so 'response.data.id' is known to TypeScript
    return this.http.post<ApiResponse<{ message: string; id: string }>>(
      `${this.apiBaseUrl}/api/safety-goals`,
      payload
    );
  }

  createSystemEffect(payload: { name: string; safety_goal_id: string; id?: string }) {
    return this.http.post<ApiResponse<{ message: string; id: string }>>(
      `${this.apiBaseUrl}/api/system-effects`,
      payload
    );
  }

  createFailureEffect(payload: { name: string; system_effect_id: string; id?: string }) {
    return this.http.post<ApiResponse<{ message: string; id: string }>>(
      `${this.apiBaseUrl}/api/failure-effects`,
      payload
    );
  }

  updateCoverage(value: { safetyMechanismId: string; failureInstanceId: string; diagnosticCoverage: number; mechanismType?: string }) {
    return this.http
      .patch<ApiResponse<{ safety_mechanism_id: string; failure_instance_id: string; diagnostic_coverage: number }>>(
        `${this.apiBaseUrl}/api/safety-mechanisms/${encodeURIComponent(value.safetyMechanismId)}/coverage`,
        {
          failure_instance_id: value.failureInstanceId,
          diagnostic_coverage: value.diagnosticCoverage,
          mechanism_type: value.mechanismType ?? 'SPF'
        }
      )
  }

  updateFailureEffectLink(value: { failureEffectId: string, systemEffectId: string }) {
    return this.http.patch<ApiResponse<FailureEffectOption>>(
      `${this.apiBaseUrl}/api/failure-effects/${encodeURIComponent(value.failureEffectId)}/link`,
      { system_effect_id: value.systemEffectId }
    );
  }

  updateSystemEffectLink(value: { systemEffectId: string; safetyGoalId: string }) {
    return this.http.patch<ApiResponse<SystemEffectOption>>(
      `${this.apiBaseUrl}/api/system-effects/${encodeURIComponent(value.systemEffectId)}/link`,
      { safety_goal_id: value.safetyGoalId }
    );
  }

  updateFailureInstanceCause(value: { failureInstanceId: string; failureEffectId: string }) {
    return this.http.patch<ApiResponse<unknown>>(
      `${this.apiBaseUrl}/api/failure-instances/${encodeURIComponent(value.failureInstanceId)}/cause`,
      { failure_effect_id: value.failureEffectId }
    );
  }

  updateComponentTemp(value: { componentDesignator: string; opTemp: number }) {
    return this.http
      .patch<ApiResponse<{ component_id: string; op_temp: number }>>(
        `${this.apiBaseUrl}/api/components/${encodeURIComponent(value.componentDesignator)}`,
        {
          op_temp: value.opTemp
        }
      );
  }

  createLink(payload: { source_id: string; target_id: string; rel_type: string }) {
    return this.http.post<ApiResponse<{ source_id: string; target_id: string; rel_type: string }>>(`${this.apiBaseUrl}/api/links`, payload);
  }

  uploadTelesisNetlist(file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<ApiResponse<TelesisNetlistUploadResult>>(
      `${this.apiBaseUrl}/api/netlists/telesis/upload`,
      formData
    );
  }

  uploadSmartPdf(file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<ApiResponse<SmartPdfUploadResult>>(
      `${this.apiBaseUrl}/api/documents/smart-pdf/upload`,
      formData
    );
  }

  assignComponentType(componentDesignator: string, componentTypeName: string, opTemp?: number) {
    const payload: { component_type_name: string; op_temp?: number } = { component_type_name: componentTypeName };
    if (opTemp !== undefined) {
      payload.op_temp = opTemp;
    }
    return this.http.patch<ApiResponse<{ id: string; component_type: string; op_temp: number }>>(
      `${this.apiBaseUrl}/api/components/${encodeURIComponent(componentDesignator)}/type`,
      payload
    );
  }

  bulkAssignComponentType(componentDesignators: string[], componentTypeName: string, opTemp?: number) {
    const payload: { component_designators: string[]; component_type_name: string; op_temp?: number } = {
      component_designators: componentDesignators,
      component_type_name: componentTypeName
    };
    if (opTemp !== undefined) {
      payload.op_temp = opTemp;
    }

    return this.http.post<ApiResponse<{
      count: number;
      component_type: string;
      components: Array<{ id: string; component_type: string; op_temp: number }>;
    }>>(`${this.apiBaseUrl}/api/components/type/bulk`, payload);
  }

  getProjects() {
    return this.http.get<ApiResponse<Project[]>>(`${this.apiBaseUrl}/api/projects`);
  }

  createProject(payload: { name: string; id?: string }, orgId?: string) {
    let url = `${this.apiBaseUrl}/api/projects`;
    if (orgId) {
      url += `?org_id=${encodeURIComponent(orgId)}`;
    }
    return this.http.post<ApiResponse<Project>>(url, payload);
  }

  deleteProject(projectId: string) {
    return this.http.delete<ApiResponse<{ message: string }>>(
      `${this.apiBaseUrl}/api/projects/${encodeURIComponent(projectId)}`
    );
  }
  /**
   * Triggers a timeline rollback for the active project set in X-Project-ID
   */
  undoLastAction(): Observable<any> {
    return this.http.post<any>(`${this.apiBaseUrl}/api/projects/undo`, {});
  }
}

